import React, { useState, useEffect } from "react";
import { Users, User } from "lucide-react";

import { X } from "lucide-react";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { addMemberToStreamChannel } from "../services/streamService";
import { db } from "../firebaseConfig";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChannelType } from "../config/stream";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

import Modal from "react-modal";
import { useGroupJoining } from "../hooks/useGroupJoining";
import PlansModal from "./PlansModal";
import { useTranslation } from "react-i18next";
Modal.setAppElement("#root");

const ExploreGroupDetailsModal = ({ group, onClose, onJoinClick }) => {
  const { user, setUser } = useAuth(); // Destructure setUser to update context

  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = async () => {
    try {
      // Fetch classes
      if (group.classIds && group.classIds?.length > 0) {
        const classesData = await Promise.all(
          group.classIds.map(async (classId) => {
            const classDoc = await getDoc(doc(db, "classes", classId));
            return classDoc.exists()
              ? { id: classDoc.id, ...classDoc.data() }
              : null;
          }),
        );
        setClasses(classesData.filter(Boolean));
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [group]);

  const renderClasses = () => {
    if (classes.length === 0) {
      return (
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500">No classes available</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {classes.map((classItem) => (
          <div key={classItem.id} className="rounded-lg bg-white p-4 shadow">
            <div className="relative mb-4 h-48 w-full overflow-hidden rounded-lg">
              <img
                src={classItem.imageUrl || "/images/panda.png"}
                alt={classItem.className}
                className="h-full w-full object-cover"
              />
            </div>
            <h3 className="mb-2 text-xl font-medium">{classItem.className}</h3>
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                {group.groupLearningLanguage}
              </span>
              <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                Advanced
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src={classItem.teacherImageUrl || "/images/panda.png"}
                  alt={classItem.teacherName}
                  className="h-6 w-6 rounded-full"
                />
                <span className="text-sm text-gray-600">
                  {classItem.teacherName} (Admin)
                </span>
              </div>
              <span className="text-sm text-gray-600">
                {classItem.maxStudents || 100}/100
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
        <div className="mx-4 w-full max-w-5xl rounded-3xl bg-white p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Group Details</h2>
            <button
              onClick={onClose}
              className="rounded-full p-2 hover:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex gap-6">
            {/* Left sidebar */}
            <div className="w-1/3 rounded-2xl bg-[#fffef0] p-6">
              <div className="flex flex-col items-center text-center">
                <img
                  src={group.imageUrl}
                  alt={group.groupName}
                  className="mb-4 h-32 w-32 rounded-full"
                />
                <h3 className="mb-2 text-2xl font-medium">{group.groupName}</h3>
                <div className="mb-2 flex items-center gap-2">
                  <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                    {group.groupLearningLanguage}
                  </span>
                  <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                    Advanced
                  </span>
                </div>
                <div className="mb-4 flex items-center gap-2">
                  <img
                    src={group.groupAdminImageUrl || "/images/panda.png"}
                    alt={group.groupAdminName}
                    className="h-6 w-6 rounded-full"
                  />
                  <span className="text-sm">
                    {group.groupAdminName} (Admin)
                  </span>
                </div>
                <p className="mb-6 text-gray-600">{group.groupDescription}</p>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1">
              {loading ? (
                <div className="flex h-64 items-center justify-center">
                  <ClipLoader color="#FFB800" size={40} />
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  {renderClasses()}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <button
              className="mt-2 w-40 rounded-full border border-black py-2 font-medium text-black"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="mt-2 w-40 rounded-full border border-black bg-[#ffbf00] py-2 font-medium text-black hover:bg-[#ffbf00]"
              onClick={onJoinClick}
            >
              Join
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const GroupCard = ({ group }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showJoinConfirmation, setShowJoinConfirmation] = useState(false);

  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const {
    groupName,
    groupLearningLanguage,
    level = "",
    groupAdminName = "Admin",
    memberIds = [],
    imageUrl,
    isPremium,
    groupAdminImageUrl,
    groupDescription,
    id: groupId,
  } = group;

  const checkValidSubscription = () => {
    // Check freeAccess flag first
    if (user?.freeAccess) {
      return true;
    }

    // Check if user has any subscriptions
    if (!user.subscriptions || user.subscriptions.length === 0) {
      return false;
    }

    // Find a valid subscription that grants premium group access
    return user.subscriptions.some((sub) => {
      if (
        !sub.startDate ||
        !sub.endDate ||
        sub.type === "None" ||
        sub.type === "none"
      ) {
        return false;
      }
      const endDate = new Date(sub.endDate.seconds * 1000);
      const startDate = new Date(sub.startDate.seconds * 1000);
      const now = new Date();

      // Check if subscription is active
      if (endDate <= now || startDate > now) {
        return false;
      }

      const type = sub.type.trim().toLowerCase();
      return (
        type === "bammbuu groups" ||
        type === "immersive exam prep" ||
        type === "bammbuu+ instructor-led group classes" ||
        type === "group_premium"
      );
    });
  };

  const handlePremiumAccess = () => {
    if (!isPremium) {
      return true;
    }

    const hasValidSubscription = checkValidSubscription();
    if (!hasValidSubscription) {
      navigate("/plans");
      return false;
    }

    return true;
  };

  const handleJoinClick = (e) => {
    e.stopPropagation(); // Prevent triggering the card click
    setShowJoinConfirmation(true);
  };

  const handleClick = () => {
    navigate(`/newGroupDetailsUser/${groupId}`);
  };

  const updateContextAndSession = (newGroupId) => {
    // Create updated user object with new group
    const updatedUser = {
      ...user,
      joinedGroups: [...(user.joinedGroups || []), newGroupId],
    };

    // Update context
    setUser(updatedUser);

    // Update session storage
    const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const updatedStoredUser = {
      ...storedUser,
      joinedGroups: [...(storedUser.joinedGroups || []), newGroupId],
    };
    sessionStorage.setItem("user", JSON.stringify(updatedStoredUser));
  };

  const { joinGroup, isProcessing } = useGroupJoining();
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);

  const handleJoinConfirm = async () => {
    console.log("[ExploreGroupCard] handleJoinConfirm called", {
      groupId,
      isPremium,
    });
    // Prevent multiple clicks
    if (isProcessing) {
      console.log("[ExploreGroupCard] Already processing");
      return;
    }

    try {
      console.log("[ExploreGroupCard] Calling joinGroup");
      const success = await joinGroup(
        user,
        { groupId, isPremium },
        updateContextAndSession,
        () => {
          // Success callback
          setShowJoinConfirmation(false);
          setShowDetailsModal(false);
        },
        (errorMessage) => {
          // Error callback
          if (
            errorMessage.includes("subscription") ||
            errorMessage.includes("plan")
          ) {
            setIsPlansModalOpen(true);
          }
        },
      );

      if (!success) {
        // Handle cancellation or failure
        return;
      }
    } catch (error) {
      console.error("Error joining group:", error);
      // Handle error appropriately - show error message to user
    }
  };

  const handleCancel = () => {
    setShowJoinConfirmation(false);
  };

  const { t } = useTranslation();

  return (
    <>
      <div
        className="mx-auto w-full max-w-sm hover:cursor-pointer"
        onClick={handleClick}
      >
        <div
          className={`h-[340px] overflow-hidden rounded-[32px] border ${
            isPremium
              ? "border-[#14B82C] bg-[#f0fdf1]"
              : "border-[#ffc310] bg-[#ffffea]"
          }`}
        >
          <div className="flex h-full flex-col items-center p-4">
            {/* Group Image */}
            <div className="relative mb-4 h-32 w-32">
              {isPremium && (
                <div className="absolute -top-3 left-1/2 z-10 w-28 -translate-x-1/2">
                  <img
                    alt="bammbuu"
                    src="/svgs/bammbuu-plus-grp-tag.svg"
                    className="w-28"
                  />
                </div>
              )}
              <img
                src={imageUrl}
                alt={groupName}
                className="h-full w-full rounded-full object-cover"
              />
            </div>

            {/* Group Name */}
            <h2
              className="mb-2 max-w-full truncate text-center text-xl font-bold text-gray-900"
              title={groupName}
            >
              {groupName}
            </h2>

            {/* Language and Level */}
            <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
              <div className="flex items-center gap-2">
                <img
                  src={
                    groupLearningLanguage === "English"
                      ? "/svgs/xs-us.svg"
                      : groupLearningLanguage === "Spanish"
                        ? "/svgs/xs-spain.svg"
                        : "/svgs/eng-spanish-xs.svg"
                  }
                  alt={
                    groupLearningLanguage === "English"
                      ? "US Flag"
                      : "Spain Flag"
                  }
                  className="h-4 w-4"
                />
                <span className="text-md max-w-[120px] truncate text-center text-green-900">
                  {groupLearningLanguage}
                </span>
              </div>
              {level !== "" && (
                <span className="rounded-full bg-[#fff885] px-3 py-1 text-xs text-gray-800">
                  {level}
                </span>
              )}
            </div>

            {/* Admin and Members */}
            <div className="mb-6 flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                {groupAdminImageUrl ? (
                  <img
                    src={groupAdminImageUrl}
                    alt={groupAdminName}
                    className="h-5 w-5 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-gray-600" />
                )}
                <span className="truncate text-xs text-gray-700">
                  {groupAdminName.length > 10
                    ? `${groupAdminName.slice(0, 10)}...`
                    : groupAdminName}{" "}
                  <span className="text-gray-500">(Admin)</span>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <img alt="user" src="/svgs/users.svg" />
                <span className="text-xs text-gray-700">
                  {memberIds.length}
                </span>
              </div>
            </div>

            {/* Join Button */}
            {!(user && memberIds.includes(user.uid)) && (
              <button
                className="mt-auto w-full rounded-full border border-black bg-[#ffbf00] py-2 font-medium text-black hover:bg-[#e5ae00]"
                onClick={handleJoinClick}
              >
                Join Group
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <ExploreGroupDetailsModal
          group={group}
          onClose={() => setShowDetailsModal(false)}
          onJoinClick={handleJoinClick}
        />
      )}

      {/* Join Confirmation Modal */}
      <Modal
        isOpen={showJoinConfirmation}
        onRequestClose={handleCancel}
        className="z-50 mx-auto mt-40 max-w-sm rounded-3xl bg-white p-6 font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div className="text-center">
          <h2 className="mb-4 text-xl font-semibold">
            {t("exploreGroupCard.confirmJoining.title")}
          </h2>
          <p className="mb-6 text-gray-600">
            {group.classType === "Individual Premium"
              ? t(
                  "exploreGroupCard.confirmJoining.descriptionPremiumIndividual",
                )
              : t("exploreGroupCard.confirmJoining.description")}
          </p>
          <div className="flex flex-row gap-2">
            <button
              className="w-full rounded-full border border-gray-300 py-2 font-medium hover:bg-gray-50"
              onClick={handleCancel}
              disabled={isProcessing}
            >
              {t("exploreGroupCard.confirmJoining.cancel")}
            </button>
            <button
              className={`w-full rounded-full border border-[#042f0c] py-2 font-medium text-black ${
                isProcessing
                  ? "cursor-not-allowed bg-gray-300"
                  : "bg-[#14b82c] hover:bg-[#119924]"
              }`}
              onClick={handleJoinConfirm}
              disabled={isProcessing}
            >
              {isProcessing
                ? t("exploreGroupCard.confirmJoining.joining")
                : t("exploreGroupCard.confirmJoining.confirm")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Plans Modal */}
      <PlansModal
        isOpen={isPlansModalOpen}
        onClose={() => setIsPlansModalOpen(false)}
      />
    </>
  );
};

export default GroupCard;
